import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { LogOut, Settings, UserRound, Moon, Sun } from "lucide-react";
import { useTheme } from "@/components/theme-provider";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { useCurrentUserRole } from "@/hooks/rbac/useCurrentUserRole";
import { useEffect, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { getCurrentUserEmail, getCurrentUserName, logout } from "@/lib/auth";

export function UserMenu() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    setMounted(true);
  }, []);

  const { data: userRole } = useCurrentUserRole();

  const handleThemeToggle = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
  };

  if (!mounted) return null;

  const displayName = getCurrentUserName();
  const email = getCurrentUserEmail();

  const handleLogout = () => {
    toast.success("Logged out successfully");
    logout();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="group relative">
          <Button
            variant="ghost"
            className={`relative h-8 p-0 rounded-full transition-all duration-300 ease-out overflow-hidden ${
              !isMobile ? 'group-hover:pr-3 group-data-[state=open]:pr-3' : ''
            }`}
          >
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarImage
                  src={`https://api.dicebear.com/7.x/lorelei/svg?seed=${email || 'default'}`}
                  alt="User avatar"
                />
                <AvatarFallback>
                  <UserRound className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
              <div className={`flex flex-col items-start transition-all duration-300 ease-out whitespace-nowrap overflow-hidden ${
                !isMobile
                  ? 'opacity-0 w-0 group-hover:opacity-100 group-hover:w-auto group-data-[state=open]:opacity-100 group-data-[state=open]:w-auto'
                  : 'opacity-0 w-0'
              }`}>
                {displayName && (
                  <span className="text-sm font-medium text-foreground leading-none">{displayName}</span>
                )}
                {userRole && (
                  <span className="text-[11px] text-muted-foreground capitalize leading-none mt-0.5">{userRole}</span>
                )}
                {!displayName && !userRole && (
                  <span className="text-sm font-medium text-foreground leading-none">Account</span>
                )}
              </div>
            </div>
            <span className="sr-only">User menu</span>
          </Button>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => navigate("/preferences")}>
          <Settings className="mr-2 h-4 w-4" />
          <span>Preferences</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
